import { Button } from '@/components/ui/button.tsx'
import { Volume2 } from 'lucide-react'
import { LibraryVocabulary } from '@/models.ts'
import useGlobalStore from '@/useGlobalStore.ts'
import { cn, pronounce } from '@/lib/utils.ts'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import useWordDetailsModalStore from '@/useWordDetailsModalStore.ts'
import LearningStageCircle from '@/components/LearningStageCircle.tsx'

const WordInList = ({
	className,
	libraryVocabulary,
	index,
	mode = 'summary',
	onWordDeleted,
}: {
	className?: string
	libraryVocabulary: LibraryVocabulary
	index: number
	mode: string
	onWordDeleted?: () => void
}) => {
	const { t } = useTranslation()
	const {
		activeChat,
		learningLanguageCode,
		isTTSSupported,
		loadLibraryOverview,
		loadAllSavedLibraryVocabulary,
		loadChatAsActiveChat,
	} = useGlobalStore()
	const { showModal, setMode, setData, setOnSuccessfulFinalAction } = useWordDetailsModalStore()

	const handleOpenWordDetailsModal = () => {
		setData(libraryVocabulary)
		setOnSuccessfulFinalAction(async () => {
			await loadLibraryOverview()
			await loadAllSavedLibraryVocabulary()
			if (activeChat !== null) {
				await loadChatAsActiveChat(activeChat.id)
			}
			if (onWordDeleted !== undefined) onWordDeleted()
		})
		setMode('saved')
		showModal()
	}

	const pronounceText = async () => {
		if (!isTTSSupported) {
			toast(t('Speaking is not supported for this language.'))
			return
		}
		await pronounce(libraryVocabulary.word, learningLanguageCode)
	}

	return (
		<div className={cn('flex justify-between', className)}>
			{mode === 'full' && (
				<div className="flex items-center justify-center mx-1 w-10">
					<span className="text-xs">{index}</span>
				</div>
			)}
			<div className="flex flex-1 active:bg-accent active:text-accent-foreground cursor-pointer">
				<div className="flex flex-none m-2 w-11" onClick={handleOpenWordDetailsModal}>
					<LearningStageCircle learningStage={libraryVocabulary.learning_stage} />
				</div>
				<div className="grow p-2" onClick={handleOpenWordDetailsModal}>
					<div>{libraryVocabulary.word}</div>
					<div className="text-muted-foreground">{libraryVocabulary.translation}</div>
				</div>
			</div>
			<div className="flex flex-none my-2 mx-2 items-center">
				<Button
					onClick={pronounceText}
					variant="ghost"
					className={cn(!isTTSSupported ? 'opacity-50' : '')}
					size="icon">
					<Volume2 className="h-5 w-5" />
				</Button>
			</div>
		</div>
	)
}

export default WordInList
